import { useTranslation } from 'react-i18next';
import {
  ChangeEvent,
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';

import { Input } from '@unified-checkout/design-system';

import type { IField } from '@app/dashboard/types/outerLayout/common';
import type {
  IPasswordFormProps,
  IPasswordPayload,
} from '@app/dashboard/types/innerLayout/profile_details/password-form';
import type { IFormActionRef } from '@app/dashboard/types/innerLayout/profile_details/footer';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import {
  addToLocalStorage,
  getFromLocalStorage,
} from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { changePassword } from '@app/dashboard/services/inner_layout/profile_details';
import { useLangStore } from '@app/dashboard/store/lang';
import { saveTokenWithExpiry } from '@app/dashboard/tools/axios/axiosUtils';
import {
  CHANGE_PASSWORD_INITIAL_STATE,
  PASSWORD_FIELDS,
} from '@app/dashboard/constants/settings/change-password';
import {
  onFieldChangeHandler,
  passwordValidations,
} from '@app/dashboard/utils/formValidations';
import { notify } from '@app/dashboard/utils/notifier';

import styles from './PasswordForm.module.scss';

const PasswordForm = forwardRef(
  ({ setFooterActions }: IPasswordFormProps, ref: Ref<IFormActionRef>) => {
    const { t } = useTranslation();
    const { lang } = useLangStore();
    const navigate = useNavigate();

    const [fields, setFields] = useState<IField>(
      CHANGE_PASSWORD_INITIAL_STATE as unknown as IField
    );

    const passwordValidationRules = useMemo(
      () =>
        passwordValidations.map((validationRule) => ({
          pattern: validationRule.pattern,
          label: validationRule.error_msg,
        })),
      [lang]
    );

    const confirmPasswordError = useMemo(() => {
      if (
        !!fields.confirmNewPassword.value &&
        fields.confirmNewPassword.value !== fields.newPassword.value
      ) {
        return t(
          'innerLayout.profileDetailsPage.changePasswordPage.passwordNotMatch'
        );
      }
    }, [fields.confirmNewPassword.value, fields.newPassword.value]);

    const handleFieldChange = (
      e: ChangeEvent<HTMLInputElement>,
      fieldName: string
    ) => {
      const targetValue = e.target.value;

      onFieldChangeHandler(fields, setFields, fieldName, targetValue, () => { });
    };

    const { mutate: mutateChangePassword, isPending } = useMutation({
      mutationFn: ({
        old_password,
        new_password,
        auth_token,
      }: IPasswordPayload) =>
        changePassword({
          old_password,
          new_password,
          auth_token,
        }),

      onSuccess(response) {
        notify.success(t(
          'innerLayout.profileDetailsPage.changePasswordPage.toasters.success'
        ));

        const accessToken = response.data.access;
        const refreshToken = response.data.refresh;
        addToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN, accessToken);
        addToLocalStorage(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
        saveTokenWithExpiry(accessToken);

        setTimeout(() => {
          navigate(INNER_ROUTES.PROFILE_DETAILS);
        }, 2000);
      },
      onError: (axiosErr: AxiosError<{ message?: string }>) => {
        let message = t('errors.somethingWentWrong');

        if (axiosErr.response?.data?.message) {
          message = axiosErr.response?.data?.message;
        } else if (axiosErr.message) {
          message = axiosErr.message;
        }
        notify.error(message);
      },
    });

    const handleCancelChanges = () => {
      setFields(CHANGE_PASSWORD_INITIAL_STATE as unknown as IField);
    };

    const handleSaveChanges = () => {
      const old_password = fields.currentPassword.value as string;
      const new_password = fields.newPassword.value as string;
      const auth_token = getFromLocalStorage(LOCAL_STORAGE_KEYS.TOKEN);
      mutateChangePassword({ old_password, new_password, auth_token });
    };

    useImperativeHandle(ref, () => ({
      handleCancelChanges,
      handleSaveChanges,
    }));

    useEffect(() => {
      const hasInvalidFields =
        Object.values(fields).some((field) => !field.isValid) ||
        !!confirmPasswordError;
      const hasValues = Object.values(fields).some((field) => !!field.value);

      setFooterActions({
        isLoading: isPending,
        shouldDisabledPrimary: hasInvalidFields,
        shouldDisabledSecondary: !hasValues,
      });
    }, [fields, isPending, confirmPasswordError]);

    return (
      <div className={styles['password-form']}>
        

        <Input
          label={t(
            'innerLayout.profileDetailsPage.changePasswordPage.form.currentPassword'
          )}
          type="password"
          placeholder={t(
            'innerLayout.profileDetailsPage.changePasswordPage.placeholders.currentPassword'
          )}
          name={PASSWORD_FIELDS.CURRENT_PASSWORD}
          onChange={(e) =>
            handleFieldChange(e, PASSWORD_FIELDS.CURRENT_PASSWORD)
          }
          value={fields.currentPassword.value}
          error={fields.currentPassword.errorMsg}
          status={fields.currentPassword.errorMsg ? 'error' : 'default'}
        />
        <Input
          label={t(
            'innerLayout.profileDetailsPage.changePasswordPage.form.newPassword'
          )}
          type="password"
          placeholder={t(
            'innerLayout.profileDetailsPage.changePasswordPage.placeholders.newPassword'
          )}
          rules={passwordValidationRules}
          name={PASSWORD_FIELDS.NEW_PASSWORD}
          onChange={(e) => handleFieldChange(e, PASSWORD_FIELDS.NEW_PASSWORD)}
          value={fields.newPassword.value}
          error={fields.newPassword.errorMsg}
          status={fields.newPassword.errorMsg ? 'error' : 'default'}
        />
        <Input
          label={t(
            'innerLayout.profileDetailsPage.changePasswordPage.form.confirmNewPassword'
          )}
          type="password"
          placeholder={t(
            'innerLayout.profileDetailsPage.changePasswordPage.placeholders.confirmNewPassword'
          )}
          name={PASSWORD_FIELDS.CONFIRM_NEW_PASSWORD}
          onChange={(e) =>
            handleFieldChange(e, PASSWORD_FIELDS.CONFIRM_NEW_PASSWORD)
          }
          value={fields.confirmNewPassword.value}
          error={fields.confirmNewPassword.errorMsg || confirmPasswordError}
          status={
            fields.confirmNewPassword.errorMsg || confirmPasswordError
              ? 'error'
              : 'default'
          }
        />
      </div>
    );
  }
);

export default PasswordForm;
